import { BusinessTypeEnum, UploadFileTypeEnum, UserBenefitType } from '@/types/constantsEnum'
import { isAllEmpty, storageLocal } from '@pureadmin/utils'
import { sessionKey } from '@/utils/auth'
import type { userType } from '@/types/userStoreTypes'
import { getToken } from '@/utils/auth'
import { CreditChangeTypeMap } from '@/config/constants'
import type { CreditItem, EnhancedCreditItem } from '@/types/apiTypes'
import { useClipboard } from '@/utils/clipboard'

export const CommonUtils = {
  getUserInfo() {
    return storageLocal().getItem<userType>(sessionKey)
  }, // 延迟
  delay(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms))
  },
  getJSON(str) {
    try {
      return JSON.stringify(str)
    } catch (e) {
      return str
    }
  },
  getJSONObj(str: string) {
    if (isAllEmpty(str)) {
      return null
    }
    try {
      return JSON.parse(str)
    } catch (e) {
      return str
    }
  },
  findBusinessFileType(businessType: number) {
    if (businessType === BusinessTypeEnum.USER_AUDIO) {
      return UploadFileTypeEnum.AUDIO
    } else if (
      // 视频
      [
        BusinessTypeEnum.USER_VIDEO,
        BusinessTypeEnum.SYSTEM_VIDEO,
        BusinessTypeEnum.USER_WEB_VIDEO,
      ].includes(businessType)
    ) {
      return UploadFileTypeEnum.VIDEO
    } else {
      // 图片
      return UploadFileTypeEnum.IMG
    }
  },
  // 获取URL参数
  getUrlParams(url?: string): Record<string, string> {
    // 如果没有传入URL，则使用当前页面的URL
    const searchStr = url ? new URL(url).search : window.location.search
    const searchParams = new URLSearchParams(searchStr)
    const params: Record<string, string> = {}

    // 遍历所有参数
    searchParams.forEach((value, key) => {
      params[key] = value
    })

    return params
  },
  // 获取指定参数值
  getUrlParam(name: string, url?: string): string | null {
    const params = this.getUrlParams(url)
    return params[name] || null
  },
  // 获取hash参数
  getHashParams(): Record<string, string> {
    const hash = window.location.hash.slice(1)
    const params: Record<string, string> = {}

    if (!hash) return params

    const searchParams = new URLSearchParams(hash)
    searchParams.forEach((value, key) => {
      params[key] = value
    })

    return params
  },
  // 防抖函数
  debounce<T extends (...args: any[]) => any>(
    fn: T,
    delay: number = 300,
  ): (...args: Parameters<T>) => Promise<ReturnType<T>> {
    let timeoutId: NodeJS.Timeout | undefined

    return (...args: Parameters<T>): Promise<ReturnType<T>> => {
      return new Promise((resolve) => {
        if (timeoutId) clearTimeout(timeoutId)

        timeoutId = setTimeout(() => {
          resolve(fn(...args))
        }, delay)
      })
    }
  },
  throttle<T extends Function>(func: T, limit: number): T {
    let inThrottle: boolean = false

    return <T>((...args: any[]) => {
      if (!inThrottle) {
        func(...args)
        inThrottle = true
        setTimeout(() => (inThrottle = false), limit)
      }
    })
  },
  getLang() {
    const lang = localStorage.getItem('language')
    switch (lang) {
      case 'zh-CN':
        return 'zh'
      case 'en':
        return 'en'
      case 'zh-TW':
        return 'zh-tw'
      default:
        return 'zh'
    }
  },
  /**
   * 保存登录账号到本地存储
   * @param account 账号
   */
  saveLoginAccount(account: string) {
    localStorage.setItem('lastLoginAccount', account)
  },

  /**
   * 根据登录类型获取本地存储的账号
   * @param isEmail 是否是邮箱登录
   * @returns 如果类型匹配则返回账号，否则返回空字符串
   */
  getLoginAccount(isEmail: boolean): string {
    const account = localStorage.getItem('lastLoginAccount')
    if (!account) return ''

    const accountIsEmail = account.includes('@')
    return accountIsEmail === isEmail ? account : ''
  },
  /**
   * 发送事件socket
   * @param event 事件
   * @param data 数据
   */
  sendEventSocket(event: string) {
    return {
      event: event,
      data: { Authorization: getToken().token },
    }
  },
}

// 导出防抖函数
export const debounce = CommonUtils.debounce

interface BenefitItem {
  category: string
  value: number
}

/**
 * 获取用户特定类型权益的值
 * @param benefits 权益列表
 * @param type 权益类型
 * @returns 对应类型的权益值，如果未找到返回0
 */
export const getBenefitValue = (benefits: BenefitItem[], type: UserBenefitType): number => {
  const benefit = benefits.find((item) => item.category === type)
  return benefit?.value ?? 0
}

/**
 * 复制分享链接
 * @param musicInfo = {
    title: string,
    artist: string,
    url: string,
    musicId: string,
  }
 */
const { copyText } = useClipboard()
export const copyShareMusicUrl = async (musicInfo: {
  title: string
  artist: string
  url: string
  musicId: string
}) => {
  const domain = window.location.origin

  const url = domain + '/home?musicId=' + musicInfo.musicId
  const shareUrl = `分享一首我在【aimusic灵镜】音乐平台生成的作品《${musicInfo.title}》【${url}】
【AIM灵境】让AI 为灵感插上翅膀，让每一粒音符都跃动惊喜，让每一次创作都突破想象，快来感受 AI 音乐的独特魅力吧！`
  await copyText(shareUrl)
}

/**
 * 获取路由参数
 * @param name 参数名
 * @returns 参数值
 */
export const getRouteParam = (name: string) => {
  const params = window.location.search
  const searchParams = new URLSearchParams(params)
  return searchParams.get(name)
}

/**
 * 过滤空字段
 * @param obj 对象
 * @returns 过滤后的对象
 */
export const filterEmptyFields = (obj: Record<string, any>) => {
  const result: Record<string, any> = {}
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (value !== '' && value !== null && value !== undefined) {
      result[key] = value
    }
  })
  return result
}
